/**
 * Customer review 11 Sep (V12): the customer's NDA, managed alongside the
 * other supplier onboarding settings. Upload or replace the document (a new
 * upload becomes the current version and every supplier is asked to sign
 * again), and see which suppliers have signed the current version and when.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { FileSignature, Loader2, Upload, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react';
import { onboardingAPI } from '../../../lib/api';
import type { NDA, NDAAgreement } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { toast } from 'sonner';

export function NDACard() {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  const canManage = Boolean(user?.is_company_admin);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState<NDA | null>(null);
  const [versions, setVersions] = useState<NDA[]>([]);
  const [agreements, setAgreements] = useState<NDAAgreement[]>([]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await onboardingAPI.getNDAs({ page_size: 50 });
      const rows = res.data.results || [];
      setVersions(rows);
      const active = rows.find((n) => n.is_active) || null;
      setCurrent(active);
      if (active) {
        const ag = await onboardingAPI.getNDAAgreements({ nda: active.id, page_size: 500 });
        setAgreements(ag.data.results || []);
      } else {
        setAgreements([]);
      }
    } catch (err: unknown) {
      console.error('Failed to load NDA:', err);
      setError(getApiErrorMessage(err, 'Failed to load the NDA'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openUpload = () => {
    setTitle(current?.title || 'Non-Disclosure Agreement');
    setVersion('');
    setFile(null);
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    if (!version.trim()) { toast.error('Enter a version label'); return; }
    if (!file) { toast.error('Choose the NDA document'); return; }
    try {
      setUploading(true);
      await onboardingAPI.uploadNDA({ title: title.trim(), version: version.trim(), file });
      toast.success(current
        ? `NDA v${version.trim()} uploaded — suppliers will be asked to sign again`
        : `NDA v${version.trim()} uploaded`);
      setUploadOpen(false);
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to upload the NDA'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSignature className="h-5 w-5" />
              NDA
              {current && (
                <Badge variant="outline" className="ml-1">Version {current.version}</Badge>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Suppliers sign your NDA once, at onboarding. Uploading a new version makes it the
              current one and every supplier is asked to sign again.
            </p>
          </div>
          {canManage ? (
            <Button size="sm" onClick={openUpload} disabled={loading}>
              <Upload className="h-4 w-4 mr-2" />
              {current ? 'Upload new version' : 'Upload NDA'}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground" title="Only a customer admin can upload or replace the NDA">
              Admin only
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-between gap-4 text-sm text-red-600">
            <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</span>
            <Button variant="outline" size="sm" onClick={load}>Retry</Button>
          </div>
        ) : !current ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileSignature className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No NDA uploaded yet. Suppliers are not asked to sign one until you upload it.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="font-medium">{current.title}</span>
              <span className="text-muted-foreground">
                Uploaded {formatDate(current.uploaded_at)}
                {current.uploaded_by_name ? ` by ${current.uploaded_by_name}` : ''}
              </span>
              {current.file_url && (
                <a
                  href={current.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open document
                </a>
              )}
              {versions.length > 1 && (
                <span className="text-xs text-muted-foreground">
                  {versions.length - 1} earlier {versions.length - 1 === 1 ? 'version' : 'versions'} retired
                </span>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                Signed by
                <Badge variant="outline">{agreements.length}</Badge>
              </h4>
              {agreements.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No supplier has signed version {current.version} yet.
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {agreements.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-4 px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                        <span className="font-medium truncate">{a.supplier_name}</span>
                        <span className="text-muted-foreground truncate">
                          {a.signatory_name}{a.signatory_title ? `, ${a.signatory_title}` : ''}
                        </span>
                      </span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        Signed {formatDate(a.signed_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={uploadOpen} onOpenChange={(open) => { if (!uploading) setUploadOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{current ? 'Upload a new NDA version' : 'Upload NDA'}</DialogTitle>
            <DialogDescription>
              {current
                ? `Version ${current.version} will be retired and every supplier asked to sign the new version.`
                : 'Suppliers you work with will be asked to sign this document.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="nda-title">Title</Label>
              <Input id="nda-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nda-version">Version</Label>
              <Input
                id="nda-version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder={current ? `e.g. ${current.version} → next` : 'e.g. 1.0'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nda-file">Document</Label>
              <Input
                id="nda-file"
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground">PDF or Word, up to 25MB.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
